import { formatRelativeTime } from '@repo/ui';
import {
  DirectorNotification,
  DirectorNotificationEventType,
} from 'domain/usecases';
import { useService } from 'presentation/app/hooks/useService';
import { useWebSocketContext } from 'presentation/app/hooks/WebSocketProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';

const PAGE_LENGTH = 8;

const eventTypeMetadata: Record<
  DirectorNotificationEventType,
  { title: string; icon: string }
> = {
  ACTIVITY_JUSTIFICATION_SUBMITTED: {
    title: 'Justificativa enviada',
    icon: 'ri-message-3-line',
  },
  DOCUMENT_SUBMISSION_REQUESTED: {
    title: 'Documento solicitado',
    icon: 'ri-file-text-line',
  },
  STUDENT_LEFT_GROUP: {
    title: 'Estudante saiu do grupo',
    icon: 'ri-user-unfollow-line',
  },
};

const upsertNotification = (
  items: DirectorNotification[],
  notification: DirectorNotification,
) => {
  const nextItems = items.filter((item) => item.id !== notification.id);
  return [notification, ...nextItems].sort(
    (firstItem, secondItem) =>
      new Date(secondItem.createdAt).getTime() -
      new Date(firstItem.createdAt).getTime(),
  );
};

const NotificationDropdown = () => {
  const { notificationsService } = useService();
  const { socket } = useWebSocketContext();
  const deliveredSocketNotificationIds = useRef<Set<string>>(new Set());
  const didPreloadNotifications = useRef(false);
  const requestsInFlight = useRef<Set<string>>(new Set());

  const [isNotificationDropdown, setIsNotificationDropdown] = useState(false);
  const [hasNewSocketNotification, setHasNewSocketNotification] =
    useState(false);
  const [notifications, setNotifications] = useState<DirectorNotification[]>(
    [],
  );
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasMore = notifications.length < total;
  const notificationCountLabel =
    total === 1 ? '1 notificação' : `${total} notificações`;

  const clearNewNotificationIndicator = useCallback(() => {
    setHasNewSocketNotification(false);
  }, []);

  const showNewNotificationIndicator = useCallback(() => {
    setHasNewSocketNotification(true);
  }, []);

  const toggleNotificationDropdown = useCallback(() => {
    setIsNotificationDropdown((currentValue) => !currentValue);
  }, []);

  const requestNotifications = useCallback(
    async ({
      nextPage = 1,
      append = false,
      showRefreshing = false,
    }: {
      nextPage?: number;
      append?: boolean;
      showRefreshing?: boolean;
    } = {}) => {
      const requestKey = `${nextPage}-${append}`;

      if (requestsInFlight.current.has(requestKey)) return;
      requestsInFlight.current.add(requestKey);

      if (append) setIsLoadingMore(true);
      if (showRefreshing) setIsRefreshing(true);

      try {
        const response = await notificationsService.getDirectorNotifications({
          page: nextPage,
          length: PAGE_LENGTH,
        });

        setNotifications((currentNotifications) =>
          append
            ? [...currentNotifications, ...(response.data || [])]
            : response.data || [],
        );
        setTotal(response.total || 0);
        setPage(nextPage);
      } catch (error) {
        if (!append) {
          setNotifications([]);
          setTotal(0);
        }
      } finally {
        if (append) setIsLoadingMore(false);
        if (showRefreshing) setIsRefreshing(false);
        requestsInFlight.current.delete(requestKey);
      }
    },
    [notificationsService],
  );

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);

    try {
      await requestNotifications({ nextPage: 1 });
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, requestNotifications]);

  useEffect(() => {
    if (didPreloadNotifications.current) return;
    didPreloadNotifications.current = true;

    requestNotifications({ nextPage: 1 });
  }, [requestNotifications]);

  useEffect(() => {
    if (!socket) return;

    const handleNotification = (notification: DirectorNotification) => {
      showNewNotificationIndicator();

      if (!deliveredSocketNotificationIds.current.has(notification.id)) {
        deliveredSocketNotificationIds.current.add(notification.id);
        socket.emit('notification:delivered', { id: notification.id });
      }

      setNotifications((currentNotifications) => {
        const notificationAlreadyExists = currentNotifications.some(
          (currentNotification) => currentNotification.id === notification.id,
        );

        if (!notificationAlreadyExists) {
          setTotal((currentTotal) => currentTotal + 1);
        }

        return upsertNotification(currentNotifications, notification);
      });
    };

    socket.on('notification', handleNotification);

    return () => {
      socket.off('notification', handleNotification);
    };
  }, [socket, showNewNotificationIndicator]);

  return (
    <Dropdown
      isOpen={isNotificationDropdown}
      toggle={toggleNotificationDropdown}
      className="topbar-head-dropdown ms-1 header-item"
    >
      <DropdownToggle
        type="button"
        tag="button"
        className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        onClick={clearNewNotificationIndicator}
      >
        <i className="bx bx-bell fs-22"></i>
        {hasNewSocketNotification && (
          <span className="position-absolute notification-badge topbar-badge-sm translate-middle p-1 bg-danger border border-light rounded-circle">
            <span className="visually-hidden">Nova notificação</span>
          </span>
        )}
      </DropdownToggle>

      <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0 notification-dropdown-menu">
        <div className="d-flex align-items-start justify-content-between gap-2 p-3 pb-2">
          <div>
            <h6 className="mb-1 fs-15 fw-semibold d-flex align-items-center gap-2">
              Notificações
            </h6>
            <p className="mb-0 fs-11 text-muted">{notificationCountLabel}</p>
          </div>

          <button
            type="button"
            className="btn btn-sm btn-icon btn-ghost-secondary rounded-circle"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Atualizar notificações"
          >
            {isRefreshing ? (
              <span
                className="spinner-border spinner-border-sm notification-refresh-spinner"
                role="status"
              >
                <span className="visually-hidden">Atualizando...</span>
              </span>
            ) : (
              <i className="ri-refresh-line" />
            )}
          </button>
        </div>

        <div className="notification-dropdown-list px-2 pb-3">
          {isRefreshing && (
            <div className="d-flex align-items-center gap-2 px-2 py-2 fs-12 text-muted notification-refreshing-row">
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              <span>Atualizando notificações...</span>
            </div>
          )}

          {notifications.length > 0 ? (
            <>
              <div className="d-grid gap-1">
                {notifications.map((notification) => {
                  const metadata = eventTypeMetadata[notification.eventType];

                  return (
                    <div
                      className="w-100 border-0 text-start p-2 notification-dropdown-item"
                      key={notification.id}
                    >
                      <span className="d-flex align-items-start gap-2 notification-dropdown-item-content">
                        <span className="avatar-xs flex-shrink-0">
                          <span className="avatar-title bg-light text-muted rounded-circle fs-16">
                            <i className={metadata.icon} />
                          </span>
                        </span>

                        <span className="d-block flex-grow-1 overflow-hidden notification-dropdown-copy">
                          <span className="d-block fs-13 fw-semibold text-body notification-dropdown-title">
                            {metadata.title}
                          </span>
                          <span className="d-block fs-13 text-muted notification-dropdown-text">
                            {notification.message}
                          </span>
                          <span className="d-block fs-11 text-muted notification-dropdown-meta">
                            {formatRelativeTime(notification.createdAt)}
                            {notification.course?.name
                              ? ` • ${notification.course.name}`
                              : ''}
                          </span>
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>

              {hasMore && (
                <button
                  type="button"
                  className="btn btn-soft-primary btn-sm w-100 mt-2"
                  onClick={() =>
                    requestNotifications({
                      nextPage: page + 1,
                      append: true,
                    })
                  }
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
                </button>
              )}
            </>
          ) : !isRefreshing ? (
            <div className="d-flex flex-column align-items-center justify-content-center text-center h-100 py-5 px-3">
              <span className="avatar-sm mb-2">
                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-4">
                  <i className="ri-notification-off-line" />
                </span>
              </span>
              <strong className="d-block text-body fs-13">
                Nenhuma notificação
              </strong>
              <span className="d-block text-muted fs-11 mt-1">
                Nenhuma notificação encontrada.
              </span>
            </div>
          ) : null}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

export default NotificationDropdown;
